'use client';
import React, { useState, useEffect } from 'react';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import { CheckCircle, XCircle, Eye, Shield, FileText, Camera, Loader2, RefreshCw } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


interface CleanerDoc {
  id: string;
  cleaner_id: string;
  cleaner_name: string;
  cleaner_email: string;
  dbs_certificate_url: string | null;
  insurance_proof_url: string | null;
  profile_photo_url: string | null;
  verification_status: 'pending' | 'under_review' | 'approved' | 'rejected';
  admin_notes: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
}

const statusVariant = (s: CleanerDoc['verification_status']) => {
  if (s === 'approved') return 'success';
  if (s === 'rejected') return 'danger';
  if (s === 'under_review') return 'assigned';
  return 'pending';
};

const statusLabel = (s: CleanerDoc['verification_status']) => {
  if (s === 'under_review') return 'Under Review';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export default function AdminOnboardingReview() {
  const [docs, setDocs] = useState<CleanerDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<CleanerDoc | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | CleanerDoc['verification_status']>('all');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const supabase = createClient();

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cleaner_documents')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setDocs(data ?? []);
    } catch (err: any) {
      toast.error(`Failed to load documents: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, []);

  const handleDecision = async (decision: 'approved' | 'rejected') => {
    if (!selected) return;
    if (decision === 'rejected' && !adminNotes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    try {
      const { error } = await supabase
        .from('cleaner_documents')
        .update({
          verification_status: decision,
          admin_notes: adminNotes.trim() || null,
          reviewed_at: new Date().toISOString(),
          reviewed_by: 'Admin',
          updated_at: new Date().toISOString(),
        })
        .eq('id', selected.id);
      if (error) throw error;
      setDocs(prev => prev.map(d => d.id === selected.id ? { ...d, verification_status: decision, admin_notes: adminNotes.trim() || null } : d));
      toast.success(`Cleaner ${decision === 'approved' ? 'approved' : 'rejected'} successfully`);
      setSelected(null);
      setAdminNotes('');
    } catch (err: any) {
      toast.error(`Action failed: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const filtered = activeFilter === 'all' ? docs : docs.filter(d => d.verification_status === activeFilter);

  const counts = {
    all: docs.length,
    pending: docs.filter(d => d.verification_status === 'pending').length,
    under_review: docs.filter(d => d.verification_status === 'under_review').length,
    approved: docs.filter(d => d.verification_status === 'approved').length,
    rejected: docs.filter(d => d.verification_status === 'rejected').length,
  };

  return (
    <>
      <div className="bg-card rounded-2xl border border-border card-shadow overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <p className="text-sm font-bold text-foreground">Cleaner Onboarding Review</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {counts.under_review} pending review · {counts.approved} approved
            </p>
          </div>
          <button
            onClick={fetchDocs}
            className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
            title="Refresh"
          >
            <RefreshCw size={15} />
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 px-4 py-3 border-b border-border overflow-x-auto">
          {([
            { key: 'all', label: 'All' },
            { key: 'under_review', label: 'Under Review' },
            { key: 'pending', label: 'Pending' },
            { key: 'approved', label: 'Approved' },
            { key: 'rejected', label: 'Rejected' },
          ] as const).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveFilter(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                activeFilter === key ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {label}
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${activeFilter === key ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'}`}>
                {counts[key]}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <Shield size={32} className="text-muted-foreground/40 mb-3" />
            <p className="text-sm font-semibold text-foreground">No applications found</p>
            <p className="text-xs text-muted-foreground mt-1">No cleaner documents match this filter</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((doc) => (
              <div key={doc.id} className="flex items-center gap-4 px-5 py-4 hover:bg-muted/30 transition-colors">
                <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {doc.cleaner_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{doc.cleaner_name}</p>
                  <p className="text-xs text-muted-foreground">{doc.cleaner_email}</p>
                </div>
                <div className="hidden sm:flex items-center gap-2">
                  <DocIndicator url={doc.dbs_certificate_url} label="DBS" icon={Shield} />
                  <DocIndicator url={doc.insurance_proof_url} label="Ins" icon={FileText} />
                  <DocIndicator url={doc.profile_photo_url} label="Photo" icon={Camera} />
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={statusVariant(doc.verification_status)} label={statusLabel(doc.verification_status)} size="sm" />
                  <button
                    onClick={() => { setSelected(doc); setAdminNotes(doc.admin_notes ?? ''); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold text-foreground hover:bg-muted transition-colors"
                  >
                    <Eye size={12} />
                    Review
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      <Modal
        open={!!selected}
        onClose={() => { setSelected(null); setAdminNotes(''); }}
        title="Review Cleaner Documents"
        size="lg"
      >
        {selected && (
          <div className="space-y-5">
            {/* Cleaner info */}
            <div className="flex items-center gap-3 p-4 bg-muted/40 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold flex-shrink-0">
                {selected.cleaner_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">{selected.cleaner_name}</p>
                <p className="text-xs text-muted-foreground">{selected.cleaner_email}</p>
              </div>
              <div className="ml-auto">
                <Badge variant={statusVariant(selected.verification_status)} label={statusLabel(selected.verification_status)} size="sm" />
              </div>
            </div>

            {/* Documents */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <DocumentCard
                label="DBS Certificate"
                icon={Shield}
                url={selected.dbs_certificate_url}
                onPreview={setPreviewUrl}
              />
              <DocumentCard
                label="Insurance Proof"
                icon={FileText}
                url={selected.insurance_proof_url}
                onPreview={setPreviewUrl}
              />
              <DocumentCard
                label="Profile Photo"
                icon={Camera}
                url={selected.profile_photo_url}
                onPreview={setPreviewUrl}
              />
            </div>

            {/* Admin notes */}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Admin Notes {selected.verification_status !== 'approved' && <span className="text-danger">*</span>}
              </label>
              <textarea
                value={adminNotes}
                onChange={e => setAdminNotes(e.target.value)}
                placeholder="Add notes for the cleaner (required for rejection)..."
                rows={3}
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-muted/30 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              />
            </div>

            {/* Actions */}
            {selected.verification_status !== 'approved' && (
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => handleDecision('rejected')}
                  disabled={processing}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-danger/40 text-danger text-sm font-semibold hover:bg-danger-bg transition-colors disabled:opacity-50"
                >
                  {processing ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                  Reject
                </button>
                <button
                  onClick={() => handleDecision('approved')}
                  disabled={processing}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 transition-all active:scale-95 disabled:opacity-50"
                >
                  {processing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                  Approve
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Document preview modal */}
      <Modal open={!!previewUrl} onClose={() => setPreviewUrl(null)} title="Document Preview" size="lg">
        {previewUrl && (
          <div className="flex items-center justify-center">
            {previewUrl.endsWith('.pdf') ? (
              <div className="text-center py-8">
                <FileText size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-3">PDF document</p>
                <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-xl gradient-primary text-white text-sm font-semibold">
                  Open PDF
                </a>
              </div>
            ) : (
              <img src={previewUrl} alt="Document preview" className="max-w-full max-h-[60vh] rounded-xl object-contain" />
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

function DocIndicator({ url, label, icon: Icon }: { url: string | null; label: string; icon: React.ElementType }) {
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${url ? 'bg-success-bg text-success' : 'bg-muted text-muted-foreground'}`}>
      <Icon size={10} />
      {label}
    </div>
  );
}

function DocumentCard({ label, icon: Icon, url, onPreview }: {
  label: string;
  icon: React.ElementType;
  url: string | null;
  onPreview: (url: string) => void;
}) {
  return (
    <div className={`rounded-xl border p-3 text-center space-y-2 ${url ? 'border-success/30 bg-success-bg/20' : 'border-border bg-muted/20'}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto ${url ? 'bg-success-bg' : 'bg-muted'}`}>
        <Icon size={16} className={url ? 'text-success' : 'text-muted-foreground'} />
      </div>
      <p className="text-xs font-semibold text-foreground">{label}</p>
      {url ? (
        <button
          onClick={() => onPreview(url)}
          className="text-xs text-primary font-semibold hover:underline flex items-center gap-1 mx-auto"
        >
          <Eye size={10} /> View
        </button>
      ) : (
        <p className="text-xs text-muted-foreground">Not uploaded</p>
      )}
    </div>
  );
}
